import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDemoModal } from "../contexts/DemoModalContext";
import PhotoGalleryModal from "../components/PhotoGalleryModal";
import {
  FaChalkboardTeacher,
  FaGlobeEurope,
  FaClock,
  FaHeadset,
  FaPenFancy,
  FaVideo,
  FaBookOpen,
  FaUsers,
  FaCertificate,
  FaLightbulb,
  FaRocket,
} from "react-icons/fa";
import { GiTeacher } from "react-icons/gi";
import AOS from "aos";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom";
import { fetchBlogPosts, getExcerpt, getFeaturedImage, fetchTestimonials, fetchMetrics, fetchFeatures, fetchCourses } from "../lib/wordpressApi";
import { getImageLoading, handleImageError } from "../lib/imageOptimization";

// Icon mapping for features
const iconMap = {
  FaHeadset: <FaHeadset />,
  FaPenFancy: <FaPenFancy />,
  GiTeacher: <GiTeacher />,
  FaChalkboardTeacher: <FaChalkboardTeacher />,
  FaGlobeEurope: <FaGlobeEurope />,
  FaBookOpen: <FaBookOpen />,
  FaVideo: <FaVideo />,
  FaClock: <FaClock />,
  FaUsers: <FaUsers />,
  FaCertificate: <FaCertificate />,
  FaLightbulb: <FaLightbulb />,
  FaRocket: <FaRocket />,
};

// Default metrics (fallback if API fails)
const defaultMetrics = [
  {
    label: "Students Trained",
    value: 5000,
    suffix: "+",
    sub: "From beginners to advanced speakers",
  },
  {
    label: "Certified Trainers",
    value: 25,
    suffix: "+",
    sub: "Language trainers & industry mentors",
  },
  {
    label: "Alumni Worldwide",
    value: 12,
    suffix: "+",
    sub: "Countries Where Alumni Are Working/Studying",
  },
  {
    label: "Success Rate",
    value: 98,
    suffix: "%",
    sub: "Success Rate in International Exams",
  },
];

// Default feature cards
const defaultFeatureCards = [
  {
    icon: "FaHeadset",
    title: "Instant Doubt Support",
    text: "24/7 chat assistance so your learning never pauses. Ask doubts anytime, get answers instantly.",
  },
  {
    icon: "FaPenFancy",
    title: "Smart Mock Tests Every 25%",
    text: "Regular milestone-based mock tests help you measure improvement, fix weaknesses, and stay exam-ready.",
  },
  {
    icon: "GiTeacher",
    title: "Free Trial Classes",
    text: "Try a real class, meet our trainers, and understand our teaching style—no risk, full clarity.",
  },
  {
    icon: "FaChalkboardTeacher",
    title: "Daily Communication Practice",
    text: "Structured speaking activities designed to eliminate hesitation, build fluency, and improve pronunciation.",
  },
  {
    icon: "FaGlobeEurope",
    title: "Globally Recognized Certifications",
    text: "Our training prepares you for CEFR, JLPT, TCF/TEF & more—accepted worldwide for study, work, and migration.",
  },
  {
    icon: "FaBookOpen",
    title: "Complete Study Material",
    text: "Concise notes, worksheets, vocab lists & exam guides curated by certified trainers.",
  },
  {
    icon: "FaVideo",
    title: "Live Interactive Classes",
    text: "No boring recordings. Engage, speak, ask doubts, and learn with real-time teacher interaction.",
  },
  {
    icon: "FaClock",
    title: "Saturday Doubt-Clearing Sessions",
    text: "Dedicated weekly sessions for revision, clarifications, and personalized guidance.",
  },
];

// Default courses (fallback if API fails)
const defaultCourses = [
  {
    name: "French Language Course (A1-B2)",
    description:
      "Speak French confidently & crack DELF/DALF with ease. Perfect for academics, travel, and careers abroad.",
    path: "/french",
    badge: "Most Popular",
  },
  {
    name: "German Language Course (A1-B2)",
    description:
      "Learn German for study, jobs, and migration. Structured lessons + exam-focused training for fast results.",
    path: "/german",
  },
  {
    name: "Spanish Language Course (A1-B2)",
    description:
      "Speak Spanish confidently through immersive classes, conversational practice, and culture-rich learning.",
    path: "/spanish",
  },
  {
    name: "Japanese Language Course (N5-N2)",
    description:
      "JLPT-aligned Japanese training with guided practice, writing support, and real-world communication drills.",
    path: "/japanese",
  },
  {
    name: "TCF / TEF Coaching",
    description:
      "Score high in TCF/TEF for Canada PR & immigration with targeted strategies, intensive mocks, and personalized feedback.",
    path: "/french",
    badge: "Premium Fast-Track",
  },
  {
    name: "Junior Master - Kids Language Program",
    description:
      "Creative, activity-based language learning for kids to build early fluency and confidence.",
    path: "/courses",
  },
];

// Default testimonials (fallback if API fails)
const defaultTestimonials = [
  {
    name: "Pratham Sanjeev Korgaonkar",
    course: "Cleared TEF on First Attempt (Canada PR)",
    quote:
      "The strategies, mock tests, and personal feedback sessions made all the difference. I achieved my target score and moved one step closer to Canada. Easily the best TEF coaching experience!",
    avatar:
      `${process.env.PUBLIC_URL}/Testimonials/Profile_img1.jpeg`,
  },
  {
    name: "Srujan Sachin Joshi",
    course: "Mastered German A1-B1 for Higher Studies in Germany",
    quote:
      "The classes were structured, interactive, and fun. The trainers ensured we actually learn the language. I never imagined I'd speak German so confidently in just a few months.",
    avatar:
      `${process.env.PUBLIC_URL}/Testimonials/Profile_img2.jpeg`,
  },
  {
    name: "Swapnaja Dinanath Musale",
    course: "From Zero to Fluent in French",
    quote:
      "I joined as a complete beginner, but the communication practice and Saturday doubt sessions helped me build fluency quickly. This institute genuinely cares about students.",
    avatar:
      `${process.env.PUBLIC_URL}/Testimonials/Profile_img3.jpeg`,
  },
  {
    name: "Arush Hemendra Ambraskar",
    course: "JLPT N5 Cleared With Ease",
    quote:
      "The Japanese faculty is outstanding. The study material and weekly revision sessions made JLPT preparation simple and manageable.",
    avatar:
      `${process.env.PUBLIC_URL}/Testimonials/Profile_img4.jpeg`,
  },
  {
    name: "Rohit Prabhakar Akula",
    course: "Spanish Fluency Achieved in Record Time",
    quote:
      "The immersive teaching approach and real-world conversation practice made Spanish feel natural. I'm now confidently using it in my travels and professional interactions.",
    avatar:
      `${process.env.PUBLIC_URL}/Testimonials/Profile_img5.jpeg`,
  },
  {
    name: "Harsh Ashok Pokale",
    course: "French B2 Certification Success",
    quote:
      "Excellent guidance throughout my learning journey. The trainers' expertise and personalized attention ensured I not only learned the language but also understood the culture deeply.",
    avatar:
      `${process.env.PUBLIC_URL}/Testimonials/Profile_img6.jpeg`,
  },
  {
    name: "Riya Maruti Dhatrak",
    course: "German Grammar Mastery & Exam Preparation",
    quote:
      "From struggling with complex grammar to acing my B1 exam, the structured curriculum and patient instructors made all the difference. Highly recommended!",
    avatar:
      `${process.env.PUBLIC_URL}/Testimonials/Profile_img7.jpeg`,
  },
];

const fallbackBlogPosts = [
  {
    title: "Why Learning a Foreign Language is Essential for Studying Abroad",
    excerpt:
      "Language proficiency isn't just a requirement—it's your key to unlocking scholarships, cultural integration, and academic success abroad.",
    image:
      "https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?auto=format&fit=crop&w=800&q=80",
    link: "/blogs/studying-abroad-language-learning",
  },
  {
    title:
      "How Foreign Language Skills Open Doors to International Career Opportunities",
    excerpt:
      "Multilingual professionals earn up to 20% more. Discover which languages offer the best ROI for your international career path.",
    image:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=800&q=80",
    link: "/blogs/international-career-opportunities",
  },
  {
    title:
      "5 Proven Strategies to Fast-Track Your Foreign Language Learning Journey",
    excerpt:
      "Achieve conversational fluency in months, not years. Learn the science-backed methods our top students use to master languages faster.",
    image:
      "https://images.unsplash.com/photo-1529070538774-1843cb3265df?auto=format&fit=crop&w=800&q=80",
    link: "/blogs/fast-track-language-learning",
  },
];

const sliderImages = [
  `${process.env.PUBLIC_URL}/Header_Background.png`,
  `${process.env.PUBLIC_URL}/Header_Background1.png`,
  `${process.env.PUBLIC_URL}/Header_Background2.png`,
];

const galleryFolders = [
  {
    id: 1,
    title: "Interactive Classroom Sessions",
    description: "Live, engaging classes with expert trainers",
    thumbnail: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=800&q=80",
    media: [
      "IMG_8796.JPG",
      "IMG_8797.JPG",
      "IMG_8798.JPG",
      "IMG_8671.JPG",
      "IMG_8679.JPG",
    ],
  },
  {
    id: 2,
    title: "Collaborative Learning",
    description: "Students practicing conversations together",
    thumbnail: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80",
    media: [
      "IMG_7890.JPG",
      "IMG_7871.JPG",
      "IMG_7808.JPG",
      "IMG_8023.JPG",
      "IMG_8041.JPG",
    ],
  },
  {
    id: 3,
    title: "Cultural Celebrations",
    description: "Celebrating language and culture together",
    thumbnail: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?auto=format&fit=crop&w=800&q=80",
    media: [
      "IMG_8636.JPG",
      "IMG_8632.JPG",
      "IMG_8629.JPG",
      "IMG_8621.JPG",
      "IMG_8619.JPG",
    ],
  },
  {
    id: 4,
    title: "Video Testimonials",
    description: "Success stories from our students",
    thumbnail: "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=800&q=80",
    media: [
      "IMG_0603.MOV",
      "IMG_0634.MP4",
      "IMG_1088.MOV",
      "IMG_1423.MP4",
      "IMG_1757.MP4",
    ],
  },
  {
    id: 5,
    title: "Learning Moments",
    description: "Focused mock tests and practice sessions",
    thumbnail: "https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?auto=format&fit=crop&w=800&q=80",
    media: [
      "IMG_6951.JPG",
      "IMG_3708.MP4",
      "IMG_3585.MP4",
      "IMG_3719.jpg",
      "IMG_3755.MOV",
    ],
  },
  {
    id: 6,
    title: "Student Achievements",
    description: "Celebrating student achievements and milestones",
    thumbnail: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=800&q=80",
    media: [
      "IMG_7661.jpg",
      "IMG_7715.jpg",
      "IMG_7739.jpg",
      "IMG_7967.jpg",
      "IMG_8003.PNG",
    ],
  },
];

const Home = () => {
  const { openModal } = useDemoModal();
  
  // Dynamic metrics state
  const [metricsConfig, setMetricsConfig] = useState([]);
  const [animatedMetrics, setAnimatedMetrics] = useState(
    defaultMetrics.map(() => 0)
  );
  const [displayIndex, setDisplayIndex] = useState(0);
  const [highlightedCourse, setHighlightedCourse] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const trackRef = useRef(null);
  const [cardOffset, setCardOffset] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const metricsRef = useRef(null);
  const [metricsVisible, setMetricsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const navigate = useNavigate();
  const [latestBlogs, setLatestBlogs] = useState(fallbackBlogPosts);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogError, setBlogError] = useState(null);
  
  // Dynamic testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);

  // Dynamic features state
  const [featureCards, setFeatureCards] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(true);

  // Dynamic courses state
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  const metricSpeeds = useMemo(
    () => metricsConfig.map((metric) => Math.ceil(metric.value / 40)),
    [metricsConfig]
  );

  const cloneCount = useMemo(
    () => Math.min(visibleCards, testimonials.length || 0),
    [visibleCards, testimonials.length]
  );

  const extendedTestimonials = useMemo(() => {
    if (!testimonials.length) {
      return [];
    }
    const head = testimonials.slice(0, cloneCount);
    const tail = testimonials.slice(-cloneCount);
    return [...tail, ...testimonials, ...head];
  }, [cloneCount, testimonials]);

  const totalActual = testimonials.length;

  const activeIndex = totalActual
    ? (((displayIndex - cloneCount) % totalActual) + totalActual) % totalActual
    : 0;

  const updateVisibleCards = useCallback(() => {
    if (window.innerWidth <= 640) {
      setVisibleCards(1);
    } else if (window.innerWidth <= 1024) {
      setVisibleCards(2);
    } else {
      setVisibleCards(3);
    }
  }, []);

  const updateCardOffset = useCallback(() => {
    if (!trackRef.current) return;

    const cards = trackRef.current.querySelectorAll(".testimonial-card");
    if (!cards.length) return;

    if (cards.length > 1) {
      const firstRect = cards[0].getBoundingClientRect();
      const secondRect = cards[1].getBoundingClientRect();
      const offset = Math.abs(secondRect.left - firstRect.left);
      if (offset > 0) {
        setCardOffset(offset);
        return;
      }
    }
    const width = cards[0].getBoundingClientRect().width;
    setCardOffset(width);
  }, []);

  useEffect(() => {
    updateVisibleCards();
    window.addEventListener("resize", updateVisibleCards);
    return () => window.removeEventListener("resize", updateVisibleCards);
  }, [updateVisibleCards]);

  useEffect(() => {
    const loadLatestBlogs = async () => {
      try {
        setBlogLoading(true);
        setBlogError(null);
        const { posts } = await fetchBlogPosts(1, 3, true);
        if (Array.isArray(posts) && posts.length) {
          const normalized = posts.map((post) => ({
            title: post?.title?.rendered || "Untitled",
            excerpt: getExcerpt(post, 160),
            image: getFeaturedImage(post, "medium_large"),
            link: `/blogs/${post?.slug || ""}`,
          }));
          setLatestBlogs(normalized);
        } else {
          setLatestBlogs(fallbackBlogPosts);
        }
      } catch (err) {
        setBlogError(err.message || "Unable to load latest blogs.");
      } finally {
        setBlogLoading(false);
      }
    };

    loadLatestBlogs();
  }, []);

  // Load dynamic testimonials from WordPress
  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        setTestimonialsLoading(true);
        const data = await fetchTestimonials(false); // Don't use cache on first load
        console.log("Fetched testimonials:", data);
        if (Array.isArray(data) && data.length > 0) {
          console.log("inside testimonail condition");
          setTestimonials(data);
        } else {
          setTestimonials(defaultTestimonials);
        }
      } catch (err) {
        console.error("Failed to load testimonials:", err.message);
        setTestimonials(defaultTestimonials);
      } finally {
        setTestimonialsLoading(false);
      }
    };

    loadTestimonials();
  }, []);

  // Load dynamic metrics from WordPress
  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const data = await fetchMetrics(true);
        console.log(data, "metrics");
        if (Array.isArray(data) && data.length > 0) {
          setMetricsConfig(data);
          // Reset animated metrics when new data loads
          setAnimatedMetrics(data.map(() => 0));
        } else {
          setMetricsConfig(defaultMetrics);
        }
      } catch (err) {
        console.warn("Failed to load metrics, using defaults:", err.message);
        setMetricsConfig(defaultMetrics);
      }
    };

    loadMetrics();
  }, []);

  // Load dynamic features from WordPress
  useEffect(() => {
    const loadFeatures = async () => {
      try {
        setFeaturesLoading(true);
        const data = await fetchFeatures(false);
        console.log("Fetched features:", data);
        if (Array.isArray(data) && data.length > 0) {
          setFeatureCards(data);
        } else {
          setFeatureCards(defaultFeatureCards);
        }
      } catch (err) {
        console.error("Failed to load features:", err.message);
        setFeatureCards(defaultFeatureCards);
      } finally {
        setFeaturesLoading(false);
      }
    };

    loadFeatures();
  }, []);

  // Load dynamic courses from WordPress
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setCoursesLoading(true);
        const data = await fetchCourses(false);
        console.log("Fetched courses:", data);
        if (Array.isArray(data) && data.length > 0) {
          setCourses(data);
        } else {
          setCourses(defaultCourses);
        }
      } catch (err) {
        console.error("Failed to load courses:", err.message);
        setCourses(defaultCourses);
      } finally {
        setCoursesLoading(false);
      }
    };

    loadCourses();
  }, []);

  useEffect(() => {
    const measure = () => updateCardOffset();
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [updateCardOffset, visibleCards, extendedTestimonials.length]);

  useEffect(() => {
    if (totalActual) {
      setDisplayIndex(cloneCount);
    } else {
      setDisplayIndex(0);
    }
  }, [cloneCount, totalActual]);

  useEffect(() => {
    if (!totalActual) return;
    const interval = setInterval(() => {
      setDisplayIndex((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [totalActual]);

  useEffect(() => {
    if (courses.length === 0) return;
    const interval = setInterval(() => {
      setHighlightedCourse((prev) => (prev + 1) % courses.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [courses.length]);

  // Intersection Observer for metrics section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setMetricsVisible(true);
            setHasAnimated(true);
          }
        });
      },
      { threshold: 1.0 }
    );

    const currentRef = metricsRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated]);

  // Animate metrics counter when section is visible
  useEffect(() => {
    if (!metricsVisible) return;

    const interval = setInterval(() => {
      setAnimatedMetrics((prev) =>
        prev.map((value, index) => {
          const nextValue = value + metricSpeeds[index];
          return nextValue >= metricsConfig[index].value
            ? metricsConfig[index].value
            : nextValue;
        })
      );
    }, 100);
    return () => clearInterval(interval);
  }, [metricsVisible, metricSpeeds]);

  // Slider auto-advance every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlideIndex(
        (prevIndex) => (prevIndex + 1) % sliderImages.length
      );
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );

    const targets = document.querySelectorAll(".reveal-on-scroll");
    targets.forEach((target) => observer.observe(target));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isTransitioning) {
      let frame1;
      let frame2;
      frame1 = requestAnimationFrame(() => {
        frame2 = requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      });
      return () => {
        if (frame1) cancelAnimationFrame(frame1);
        if (frame2) cancelAnimationFrame(frame2);
      };
    }
    return undefined;
  }, [isTransitioning]);

  const handleTrackTransitionEnd = useCallback(() => {
    if (!totalActual) return;

    if (displayIndex >= totalActual + cloneCount) {
      setIsTransitioning(false);
      setDisplayIndex(cloneCount);
    } else if (displayIndex < cloneCount) {
      setIsTransitioning(false);
      setDisplayIndex(totalActual + cloneCount - 1);
    }
  }, [displayIndex, cloneCount, totalActual]);

  const handleDotClick = useCallback(
    (index) => {
      if (!totalActual) return;
      setIsTransitioning(true);
      setDisplayIndex(cloneCount + index);
    },
    [cloneCount, totalActual]
  );

  const handleCardClick = useCallback(
    (index) => {
      if (!totalActual) return;
      const normalized =
        (((index - cloneCount) % totalActual) + totalActual) % totalActual;
      setIsTransitioning(true);
      setDisplayIndex(cloneCount + normalized);
    },
    [cloneCount, totalActual]
  );

  const trackIndex = displayIndex;

  return (
    <div className="bg-canvas text-ink relative overflow-x-hidden">
      {/* subtle pattern */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.07]">
        <div className="h-full w-full bg-[radial-gradient(circle,_rgba(255,255,255,0.4)_1px,_transparent_0)] bg-[length:22px_22px]" />
      </div>

      {/* HERO */}
      <section className="relative w-full h-[85vh] min-h-[520px] overflow-hidden">
        {/* Slider Images */}
        {sliderImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
              currentSlideIndex === index ? "opacity-100" : "opacity-0"
            }`}
            style={{
              backgroundImage: `url('${image}')`,
              backgroundPosition: "center right",
            }}
          />
        ))}

        {/* CONTENT */}
        <div className="relative z-20 h-full flex items-center">
          <div className="w-full px-4 sm:px-6 lg:px-16 xl:px-24">
            <div
              className="
          max-w-xl lg:max-w-2xl
          rounded-2xl lg:rounded-none
          bg-white/80 lg:bg-transparent
          backdrop-blur-md lg:backdrop-blur-0
          p-6 sm:p-8 lg:p-0
        "
            >
              {/* Heading */}
              <h1 className="text-2xl sm:text-3xl lg:text-5xl font-extrabold leading-tight text-[#0f1226]">
                The world’s most powerful learning platform –
                <br className="hidden sm:block" />
                forging global careers, one learner at a time.
              </h1>

              {/* Description */}
              <p className="mt-4 text-sm sm:text-base lg:text-lg text-[#2a2f4a] font-medium leading-relaxed">
                Master French, German, Spanish, Japanese & more with the most
                trusted language training academy. International curriculum,
                expert trainers, guaranteed progress.
              </p>

              {/* CTAs */}
              <div className="mt-6 flex flex-wrap gap-4">
                <span
                  onClick={() => {
                    navigate("/courses");
                  }}
                  className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-lg transition-all hover:-translate-y-1 hover:scale-105 cursor-pointer"
                >
                  Explore Language Courses
                </span>

                <button
                  onClick={() => openModal()}
                  className="inline-flex items-center justify-center rounded-full border-2 border-primary px-6 py-3 text-sm font-semibold uppercase tracking-wide text-primary bg-white transition-all hover:bg-primary hover:text-white hover:-translate-y-1 hover:scale-105"
                >
                  Book a Free Demo
                </button>
              </div>

              {/* Highlights */}
              {/* <div className="mt-6 flex flex-wrap gap-3">
          {[
            "Exam-focused batches (DELF, Goethe, JLPT, DELE)",
            "Small groups with live speaking practice",
          ].map((item) => (
            <div
              key={item}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold text-[#0f1226] border border-primary/20 shadow-sm"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {item}
            </div>
          ))}
        </div> */}

              {/* Trust Badges */}
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-primary/20 flex flex-wrap items-center gap-3 sm:gap-4 md:gap-6">
                {/* Rating Badge */}
                <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-primary/30 shadow-md">
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-500 text-base sm:text-lg">★★★★★</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs sm:text-sm font-bold text-primary">
                      4.9/5
                    </span>
                    <span className="text-[9px] sm:text-[10px] text-[#0f1226]/70 font-medium">
                      Rated by Students
                    </span>
                  </div>
                </div>

                {/* Learners Badge */}
                <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-primary/30 shadow-md">
                  <div className="flex flex-col">
                    <span className="text-xs sm:text-sm font-bold text-primary">
                      5000+
                    </span>
                    <span className="text-[9px] sm:text-[10px] text-[#0f1226]/70 font-medium">
                      Learners Trained
                    </span>
                  </div>
                </div>

                {/* Exam Prep Badge */}
                <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-primary/30 shadow-md">
                  <div className="flex flex-col">
                    <span className="text-[9px] sm:text-[10px] text-[#0f1226]/70 font-medium uppercase tracking-wide">
                      Official Exam Prep
                    </span>
                    <span className="text-[10px] sm:text-xs font-bold text-primary">
                      DELF | Goethe | JLPT | DELE
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-1.5 sm:gap-2">
          {sliderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlideIndex(index)}
              className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                currentSlideIndex === index
                  ? "w-6 sm:w-8 bg-white"
                  : "w-1.5 sm:w-2 bg-white/60 hover:bg-white"
              }`}
            />
          ))}
        </div>
      </section>

      {/* VALUE PROPOSITION SECTION */}
      <section className="relative z-10 py-12 sm:py-16 bg-gradient-to-br from-primary/5 via-white to-primary-soft/5">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div
            className="text-center"
            data-aos="fade-up"
            data-aos-duration="1000"
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0f1226] leading-tight">
              TLA isn't Just a Language Institute —
              <br className="hidden sm:block" />
              <span className="text-primary">
                {" "}
                It's Your Gateway to Global Opportunities.
              </span>
            </h2>
            <p className="mt-4 sm:mt-6 text-sm sm:text-base lg:text-lg text-[#2a2f4a] leading-relaxed max-w-3xl mx-auto px-2">
              We combine expert instruction, immersive learning, and real-world
              speaking practice so you not only learn the language —{" "}
              <span className="font-semibold text-primary">you live it.</span>
            </p>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section ref={metricsRef} className="w-full relative z-10 py-10 sm:py-12">
        <div className="pointer-events-none absolute left-1/2 top-[32rem] h-40 w-40 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl sm:left-24 sm:translate-x-0" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-2 sm:gap-3 pb-3 sm:pb-4 text-center sm:text-left">
            <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[#0f1226]">
              Trusted by learners around the world
            </p>
          </div>
        </div>
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 px-4 sm:px-6 lg:px-8">
          {metricsConfig.map((metric, index) => (
            <div
              key={metric.label}
              data-aos="fade-up"
              data-aos-delay={100 + index * 100}
              data-aos-duration="800"
              className="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-primary/20 bg-white/5 backdrop-blur-sm p-4 sm:p-6 shadow-elevated transition hover:-translate-y-1 hover:shadow-float hover:bg-white/10"
            >
              <span className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#0f1226] via-[#1a1f3a] to-[#0f1226] transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative space-y-1 ">
                <span className="block text-2xl sm:text-3xl font-bold text-primary drop-shadow-sm">
                  {animatedMetrics[index]}
                  {metric.suffix}
                </span>
                <span className="block text-base sm:text-lg font-semibold text-white">
                  {metric.label}{" "}
                </span>
                <span className="text-[10px] sm:text-xs text-white/70">{metric.sub}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 sm:py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary"
          >
            Why Choose Us
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink"
          >
            Experience a Dynamic, Learner-First Ecosystem
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-xs sm:text-sm lg:text-base leading-relaxed text-ink-muted px-2"
          >
            Our programs blend live interaction, smart analytics, and
            exam-aligned practice to ensure you stay motivated and achieve your
            language goals faster.
          </p>
        </div>

        {featuresLoading && (
          <div className="mt-12 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
          </div>
        )}

        {!featuresLoading && featureCards.length === 0 && (
          <div className="mt-12 mx-auto max-w-md px-4">
            <div className="rounded-lg border border-primary/10 bg-white px-4 py-6 text-center text-ink-muted">
              No features available yet.
            </div>
          </div>
        )}

        {!featuresLoading && featureCards.length > 0 && (
          <div className="mx-auto mt-10 sm:mt-12 grid w-full max-w-6xl grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 px-4 sm:px-6 lg:px-8">
          {featureCards.map((card, index) => (
            <article
              key={card.title}
              data-aos="zoom-in"
              data-aos-delay={300 + index * 100}
              data-aos-duration="800"
              className="group relative overflow-hidden rounded-2xl border border-primary/10 bg-gradient-to-br from-white via-white to-primary/10 p-6 shadow-elevated transition hover:-translate-y-2 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)]"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-xl text-primary animate-icon-bounce relative z-10">
                {iconMap[card.icon] || <FaBookOpen />}
              </span>
              <h3 className="mt-4 text-lg font-semibold text-ink relative z-10">
                {card.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-muted relative z-10">
                {card.text}
              </p>
              <div className="pointer-events-none absolute inset-0 translate-y-6 bg-gradient-to-br from-primary/15 to-primary-soft/12 opacity-0 transition group-hover:translate-y-0 group-hover:opacity-100" />
            </article>
          ))}
        </div>
        )}
      </section>

      {/* COURSES */}
      <section className="bg-white py-16 sm:py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary"
          >
            Our Courses
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold"
          >
            Highlight Languages Tailored to Your Ambition
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-xs sm:text-sm lg:text-base leading-relaxed text-ink-muted px-2"
          >
            Dive into curated roadmaps for exams like DELF, Goethe, HSK, JLPT,
            and more. Choose a track and we will personalize a path that fits
            your schedule.
          </p>
        </div>

        {coursesLoading && (
          <div className="mt-12 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
          </div>
        )}

        {!coursesLoading && courses.length === 0 && (
          <div className="mt-12 mx-auto max-w-md px-4">
            <div className="rounded-lg border border-primary/10 bg-white px-4 py-6 text-center text-ink-muted">
              No courses available yet.
            </div>
          </div>
        )}

        {!coursesLoading && courses.length > 0 && (
          <div
          className="mx-auto mt-10 sm:mt-12 flex w-full max-w-6xl flex-wrap justify-center gap-4 sm:gap-6 px-4 sm:px-6 lg:px-8"
          data-aos="fade-up"
          data-aos-delay="100"
          data-aos-duration="800"
        >
          {courses.map((course, index) => (
            <article
              key={`${course.name}-${index}`}
              className={`group relative w-full overflow-hidden rounded-xl sm:rounded-2xl border border-transparent bg-gradient-to-br from-white via-white to-primary/5 p-5 sm:p-6 shadow-elevated transition-all duration-300 hover:-translate-y-2 hover:border-primary/30 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)] hover:z-20 sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)] ${
                highlightedCourse === index
                  ? "border-primary/30 shadow-[0_32px_60px_rgba(79,64,198,0.25)] z-10"
                  : "z-0"
              }`}
            >
              <span className="pointer-events-none absolute -right-12 -top-12 h-24 w-24 rounded-full bg-primary/10 blur-2xl opacity-0 transition duration-300 group-hover:opacity-100" />
              {course.badge && (
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 sm:px-3 py-0.5 sm:py-1 text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.12em] sm:tracking-[0.15em] text-primary">
                  {course.badge}
                </span>
              )}
              <h3 className="mt-2 text-base sm:text-lg lg:text-xl font-semibold text-ink">
                {course.name}
              </h3>
              <p className="mt-2 sm:mt-3 text-xs sm:text-sm leading-relaxed text-ink-muted">
                {course.description}
              </p>
              <Link
                className="mt-4 sm:mt-6 inline-flex items-center text-xs sm:text-sm font-semibold text-primary transition hover:text-primary/80"
                to={course.path}
              >
                Learn More →
              </Link>
              {highlightedCourse === index && (
                <span className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-primary/20 animate-highlight-pulse" />
              )}
            </article>
          ))}
        </div>
        )}
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-canvas pt-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-6">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary"
          >
            Success Stories
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-4 text-3xl font-semibold sm:text-4xl"
          >
            Results That Speak Louder Than Promises.
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-4 text-sm leading-relaxed text-ink-muted sm:text-base"
          >
            At The Lingua Alliance, success isn’t a promise - it’s a pattern.
            Here are a few stories from learners who turned language skills into
            life-changing opportunities.
          </p>
        </div>

        {testimonialsLoading && (
          <div className="mt-12 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
          </div>
        )}

        {!testimonialsLoading && testimonials.length === 0 && (
          <div className="mt-12 mx-auto max-w-md px-4">
            <div className="rounded-lg border border-primary/10 bg-white px-4 py-6 text-center text-ink-muted">
              No testimonials available yet.
            </div>
          </div>
        )}

        {!testimonialsLoading && testimonials.length > 0 && (
          <>
            <div className="relative mt-12 overflow-hidden px-4 sm:px-6">
          <div
            ref={trackRef}
            className="flex gap-6 sm:gap-8 transition-transform duration-[700ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] pb-4"
            style={{
              transform:
                cardOffset > 0
                  ? `translateX(-${cardOffset * trackIndex}px)`
                  : undefined,
              transition: isTransitioning ? undefined : "none",
            }}
            onTransitionEnd={handleTrackTransitionEnd}
          >
            {extendedTestimonials.map((item, index) => {
              const normalized = totalActual
                ? (((index - cloneCount) % totalActual) + totalActual) %
                  totalActual
                : 0;
              const isActive = normalized === activeIndex;
              return (
                <article
                  key={`${item.name}-${index}`}
                  className={`testimonial-card flex w-full flex-shrink-0 flex-col rounded-2xl border border-primary/10 bg-white p-8 text-center shadow-elevated transition hover:-translate-y-1 hover:shadow-float sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] ${
                    isActive
                      ? "border-primary/30 shadow-[0_24px_48px_rgba(79,64,198,0.25)]"
                      : "opacity-80"
                  }`}
                  style={{ animationDelay: `${normalized * 0.12}s` }}
                  onClick={() => handleCardClick(index)}
                >
                  <div className="relative mx-auto">
                    <span className="pointer-events-none absolute inset-0 rounded-full bg-primary/15 blur-xl" />
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="relative mx-auto h-24 w-24 rounded-full border-4 border-primary/20 object-cover"
                    />
                  </div>
                  <blockquote className="mt-4 text-sm italic leading-relaxed text-ink-muted">
                    “{item.quote}”
                  </blockquote>
                  <h4 className="mt-4 text-lg font-semibold text-ink">
                    {item.name}
                  </h4>
                  <span className="text-sm font-medium text-primary">
                    {item.course}
                  </span>
                </article>
              );
            })}
          </div>
        </div>
        <div className="mt-6 flex justify-center gap-3">
          {testimonials.map((_, index) => (
            <button
              type="button"
              key={index}
              className={`h-2.5 w-2.5 rounded-full transition ${
                activeIndex === index
                  ? "bg-primary"
                  : "bg-primary/25 hover:bg-primary/60"
              }`}
              onClick={() => handleDotClick(index)}
              aria-label={`Show testimonial ${index + 1}`}
            />
          ))}
        </div>
          </>
        )}
      </section>

      {/* BRAND BADGES */}
      <section className="py-4 sm:py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary"
          >
            Brand Partners & Certifications
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold"
          >
            Your Learning, Powered by Global Affiliations.
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-xs sm:text-sm lg:text-base leading-relaxed text-ink-muted px-2"
          >
            At The Lingua Alliance, every program you join is powered by trusted
            partnerships and internationally accepted certification frameworks.{" "}
            <br className="hidden sm:block" />
            This means your training, your exams, and your certificates carry
            real value worldwide.
          </p>
        </div>
        {/* <div className="mx-auto mt-12 grid w-full max-w-6xl grid-cols-1 gap-6 px-4 sm:grid-cols-2 lg:grid-cols-4 lg:px-6">
          {brandBadges.map((badge, index) => (
            <article
              key={badge.label}
              data-aos="zoom-in"
              data-aos-delay={300 + index * 100}
              data-aos-duration="800"
              className="rounded-2xl border border-dashed border-primary/20 bg-gradient-to-br from-white via-white to-primary/10 p-6 text-center transition hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_26px_52px_rgba(18,10,56,0.18)]"
            >
              <div className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-sm font-semibold text-primary">
                {badge.label}
              </div>
              <p className="mt-3 text-sm text-ink-muted">{badge.description}</p>
            </article>
          ))}
        </div> */}

        {/* Global Alignments Section */}
        <div className="mx-auto mt-12 sm:mt-16 max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#0f1226] mb-2 sm:mb-3">
              🌐 Our Global Alignments
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* CEFR */}
            <div
              data-aos="fade-up"
              data-aos-delay="100"
              data-aos-duration="800"
              className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-primary/20 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <h4 className="text-base sm:text-lg font-bold text-primary mb-2 sm:mb-3">
                CEFR - European Framework for French, German, Spanish
              </h4>
              <p className="text-xs sm:text-sm text-ink-muted leading-relaxed">
                Your levels (A1-B2) follow the official European benchmark
                recognized by universities, embassies, and employers worldwide.
              </p>
            </div>

            {/* JLPT */}
            <div
              data-aos="fade-up"
              data-aos-delay="200"
              data-aos-duration="800"
              className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-primary/20 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <h4 className="text-base sm:text-lg font-bold text-primary mb-2 sm:mb-3">
                JLPT - Japanese Language Proficiency Test (N5-N2)
              </h4>
              <p className="text-xs sm:text-sm text-ink-muted leading-relaxed">
                Training aligned with Japan's most respected language evaluation
                system - perfect for education and job opportunities.
              </p>
            </div>

            {/* TCF/TEF */}
            <div
              data-aos="fade-up"
              data-aos-delay="300"
              data-aos-duration="800"
              className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-primary/20 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <h4 className="text-base sm:text-lg font-bold text-primary mb-2 sm:mb-3">
                TCF / TEF Exam Alignment (Canada PR & Immigration)
              </h4>
              <p className="text-xs sm:text-sm text-ink-muted leading-relaxed">
                Our premium TCF/TEF program follows Paris-based exam standards,
                helping students achieve high scores for immigration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BLOGS */}
      <section className="bg-white py-16 sm:py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary"
          >
            Latest Blogs
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold"
          >
            Insights to Build Momentum
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-xs sm:text-sm lg:text-base leading-relaxed text-ink-muted px-2"
          >
            Stay inspired with curated reads covering study hacks, certification
            tips, and motivational stories from our community.
          </p>
        </div>
        <div className="mx-auto mt-10 sm:mt-12 grid w-full max-w-6xl grid-cols-1 gap-5 sm:gap-6 px-4 sm:px-6 md:grid-cols-2 xl:grid-cols-3 lg:px-8">
          {blogError && (
            <div className="col-span-full rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {blogError}
            </div>
          )}

          {blogLoading && (
            <div className="col-span-full flex justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
            </div>
          )}

          {!blogLoading && !blogError && latestBlogs.length === 0 && (
            <div className="col-span-full rounded-lg border border-primary/10 bg-white px-4 py-6 text-center text-ink-muted">
              No blog posts available yet.
            </div>
          )}

          {!blogLoading && !blogError && latestBlogs.map((blog, index) => (
            <article
              key={blog.title || index}
              data-aos="fade-up"
              data-aos-delay={300 + index * 100}
              data-aos-duration="800"
              className="overflow-hidden rounded-xl sm:rounded-2xl bg-white shadow-elevated transition hover:-translate-y-1 hover:shadow-float"
            >
              <div className="h-40 sm:h-48 w-full overflow-hidden bg-gradient-to-br from-primary/10 to-primary-soft/10">
                {blog.image ? (
                  <img
                    src={blog.image}
                    loading={getImageLoading(index)}
                    onError={(e) => handleImageError(e)}
                    alt={blog.title}
                    className="h-full w-full object-cover transition duration-500 hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-primary">No image</div>
                )}
              </div>
              <div className="flex h-full flex-1 flex-col gap-3 sm:gap-4 p-5 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-ink">{blog.title}</h3>
                <p className="text-xs sm:text-sm leading-relaxed text-ink-muted">
                  {blog.excerpt}
                </p>
                <a
                  className="inline-flex items-center text-xs sm:text-sm font-semibold text-primary transition hover:text-primary/80"
                  href={blog.link || "/blogs"}
                >
                  Read More →
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>



      {/* PHOTO GALLERY */}
      <section className="bg-canvas py-16 sm:py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary"
          >
            Photo Gallery
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold"
          >
            Experience the Energy of The Lingua Alliance
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-3 sm:mt-4 text-xs sm:text-sm lg:text-base leading-relaxed text-ink-muted px-2"
          >
            A glimpse into our classrooms, culture, and community - where real learning and real connections happen every day.
          </p>
        </div>
        <div className="mx-auto mt-10 sm:mt-12 grid w-full max-w-6xl grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 px-4 sm:px-6 lg:px-8">
          {galleryFolders.map((folder, index) => (
            <article
              key={folder.id}
              onClick={() => {
                setSelectedFolder(folder);
                setGalleryModalOpen(true);
              }}
              data-aos="zoom-in"
              data-aos-delay={300 + index * 100}
              data-aos-duration="800"
              className="group relative overflow-hidden rounded-xl sm:rounded-2xl shadow-elevated transition hover:-translate-y-2 hover:shadow-float cursor-pointer"
            >
              <div className="h-48 sm:h-56 md:h-64 w-full overflow-hidden">
                <img
                  src={folder.thumbnail}
                  alt={folder.title}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 transition duration-300 group-hover:opacity-100" />
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 translate-y-6 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                <h3 className="text-base sm:text-lg font-semibold text-white">{folder.title}</h3>
                <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-white/90">{folder.description}</p>
                <p className="mt-2 text-xs font-semibold text-primary">Click to view {folder.media.length} items →</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <PhotoGalleryModal
        isOpen={galleryModalOpen}
        onClose={() => setGalleryModalOpen(false)}
        folder={selectedFolder}
      />

      {/* CTA */}
      <section className="bg-gradient-to-r from-primary to-primary-soft text-white relative z-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 sm:gap-6 px-4 sm:px-6 py-10 sm:py-12 text-center lg:flex-row lg:px-8 lg:text-left">
          <div
            data-aos="fade-right"
            data-aos-duration="1000"
            className="max-w-xl"
          >
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold">
              Ready to start your language journey?
            </h2>
            <p className="mt-2 sm:mt-3 text-xs sm:text-sm lg:text-base leading-relaxed text-white/80">
              Share your goals and schedule, and our academic counsellors will
              suggest the perfect batch and exam plan for you.
            </p>
          </div>
          <div
            data-aos="fade-left"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3"
          >
            <button
              onClick={() => openModal()}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-full bg-white px-6 sm:px-7 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] sm:tracking-[0.18em] text-primary shadow-[0_18px_30px_rgba(15,23,42,0.35)] transition hover:-translate-y-1 hover:bg-white"
            >
              Book a Free Demo
            </button>
            <button
              onClick={() => {
                navigate("/courses")
              }}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-full border border-white/70 px-6 sm:px-7 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] sm:tracking-[0.18em] cursor-pointer text-white transition hover:-translate-y-1 hover:bg-white/10"
            >
              View All Courses
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
